// src/components/clinica/FormularioClinico.js
// =========================================================================
// 📋 FICHA CLÍNICA SEGÚN EL TIPO DE CLÍNICA
//
// Sustituye al antiguo formulario SOAP fijo: las pestañas y los campos salen
// de la plantilla de la especialidad de la consulta (medicina general,
// fisioterapia, odontología…). Mantiene el mismo candado legal: una consulta
// completada, o de hace más de 18 horas, es de solo lectura.
//
// Los campos «base» (motivo, diagnóstico, tratamiento…) se guardan en sus
// columnas de siempre; los propios de la especialidad van en datosEspecialidad
// (cifrado en el servidor).
// =========================================================================
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { conVersion } from '../../utils/concurrencia';
import { useClinica } from '../../context/ClinicaContext';
import CampoClinico from './CampoClinico';

import SaveIcon from '@mui/icons-material/Save';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ScienceIcon from '@mui/icons-material/Science';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GavelIcon from '@mui/icons-material/Gavel';

const ICONOS = {
    subjetivo: <LocalHospitalIcon fontSize="small" />,
    evaluacion: <AssignmentIcon fontSize="small" />,
    plan: <ScienceIcon fontSize="small" />
};

const vacio = (v) => v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0)
    || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

/** IMC = peso (kg) / talla (m)², con un decimal. */
const calcularImc = (peso, talla) => {
    if (!(peso > 0) || !(talla > 0)) return '';
    return Math.round((peso / ((talla / 100) ** 2)) * 10) / 10;
};

function estadoInicial(consulta, secciones) {
    const base = {};
    const datos = {};
    for (const seccion of secciones) {
        for (const campo of seccion.campos) {
            if (campo.base) {
                const v = consulta?.[campo.id];
                base[campo.id] = campo.tipo === 'constantes' ? { ...(v || {}) } : (v || '');
            } else {
                const v = consulta?.datosEspecialidad?.[campo.id];
                datos[campo.id] = v ?? '';
            }
        }
    }
    return { base, datos };
}

const FormularioClinico = ({ consultaOriginal, alGuardar, mostrarToast }) => {
    const { definicion } = useClinica();
    const plantilla = definicion(consultaOriginal?.especialidad);
    const secciones = useMemo(() => plantilla?.historia?.secciones || [], [plantilla]);

    const [tabActiva, setTabActiva] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [form, setForm] = useState(() => estadoInicial(consultaOriginal, secciones));

    // Candado legal (igual que siempre): firmada o más de 18 h desde la cita
    const horasPasadas = consultaOriginal?.fecha
        ? (new Date() - new Date(consultaOriginal.fecha)) / (1000 * 60 * 60)
        : 0;
    const caducadaPorTiempo = horasPasadas > 18 && consultaOriginal?.estado !== 'completada';
    const isReadOnly = consultaOriginal?.estado === 'completada' || caducadaPorTiempo;

    // Se reinicia solo cuando cambia la consulta (evita arrastrar datos de otra)
    const consultaId = consultaOriginal?.id || consultaOriginal?._id;
    useEffect(() => {
        setForm(estadoInicial(consultaOriginal, secciones));
        setTabActiva(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consultaId, secciones]);

    const cambiar = (campo, valor) => {
        if (isReadOnly) return;
        setForm(prev => {
            if (campo.base) return { ...prev, base: { ...prev.base, [campo.id]: valor } };
            const datos = { ...prev.datos, [campo.id]: valor };
            const campoImc = secciones.flatMap(s => s.campos).find(c => c.calculado === 'imc');
            if (campoImc && (campo.id === 'peso' || campo.id === 'talla')) {
                datos[campoImc.id] = calcularImc(datos.peso, datos.talla);
            }
            return { ...prev, datos };
        });
    };

    const valorDe = (campo) => (campo.base ? form.base[campo.id] : form.datos[campo.id]);

    const handlePreSubmit = () => {
        if (isReadOnly) return mostrarToast('El documento está bloqueado legalmente.', 'error');

        for (let i = 0; i < secciones.length; i++) {
            const falta = secciones[i].campos.find(c => c.obligatorio && vacio(typeof valorDe(c) === 'string' ? valorDe(c).trim() : valorDe(c)));
            if (falta) {
                setTabActiva(i);
                return mostrarToast(`Falta un dato obligatorio: ${falta.etiqueta}.`, 'warning');
            }
        }
        setShowConfirmModal(true);
    };

    const handleConfirmSubmit = async () => {
        setShowConfirmModal(false);
        if (!consultaId) return mostrarToast('Error: identificador de consulta ausente.', 'error');

        setLoading(true);
        try {
            const limpiar = (v) => (typeof v === 'string' ? v.trim() : v);
            const payload = { estado: 'completada', recetas: consultaOriginal?.recetas || [] };

            for (const [clave, valor] of Object.entries(form.base)) {
                payload[clave] = clave === 'constantesVitales'
                    ? Object.fromEntries(Object.entries(valor || {}).map(([k, v]) => [k, limpiar(v) || '']))
                    : limpiar(valor) || '';
            }

            const datosEspecialidad = {};
            for (const [clave, valor] of Object.entries(form.datos)) {
                const v = limpiar(valor);
                if (!vacio(v)) datosEspecialidad[clave] = v;
            }
            payload.datosEspecialidad = datosEspecialidad;

            const response = await api.put(`/consultas/${consultaId}`, payload, conVersion(consultaOriginal));
            mostrarToast('Acto clínico firmado y sellado con éxito', 'success');
            if (alGuardar) alGuardar(response);
        } catch (error) {
            // api ya traduce el error del servidor a un mensaje legible
            mostrarToast(error.message || 'Error al guardar la ficha clínica', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!plantilla) {
        return (
            <div className="p-8 bg-white border border-slate-200 rounded-3xl text-sm font-semibold text-slate-500">
                Cargando la plantilla de la ficha clínica…
            </div>
        );
    }

    const bloqueoPorTiempo = caducadaPorTiempo && consultaOriginal?.estado !== 'completada';

    return (
        <div className="p-6 md:p-8 bg-white border border-slate-200 rounded-3xl shadow-sm relative">

            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">{plantilla.historia.titulo}</h2>
                    <p className="text-xs font-bold uppercase tracking-widest mt-1" style={{ color: plantilla.color }}>{plantilla.nombre}</p>
                </div>
                {isReadOnly && (
                    <span className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg flex items-center gap-1.5 border shrink-0 ${bloqueoPorTiempo ? 'bg-red-100 text-red-800 border-red-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}>
                        {bloqueoPorTiempo ? <WarningAmberIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                        {bloqueoPorTiempo ? 'Caducada por tiempo' : 'Sellado legal activado'}
                    </span>
                )}
            </div>

            {isReadOnly && (
                <div className={`mb-8 p-4 border rounded-2xl flex items-start gap-3 animate-fade-in ${bloqueoPorTiempo ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                    {bloqueoPorTiempo ? <WarningAmberIcon className="text-red-500 mt-0.5" /> : <LockIcon className="text-amber-500 mt-0.5" />}
                    <div>
                        <h4 className={`text-sm font-black uppercase tracking-wide ${bloqueoPorTiempo ? 'text-red-900' : 'text-amber-900'}`}>
                            {bloqueoPorTiempo ? 'Bloqueada por inactividad' : 'Acto clínico firmado'}
                        </h4>
                        <p className={`text-xs font-semibold mt-1 leading-relaxed ${bloqueoPorTiempo ? 'text-red-700' : 'text-amber-700'}`}>
                            {bloqueoPorTiempo
                                ? 'Han pasado más de 18 horas desde la fecha de esta cita y el sistema la ha bloqueado automáticamente por seguridad. Debe iniciar un nuevo acto clínico.'
                                : 'Por cumplimiento normativo (inmutabilidad de los registros clínicos), este documento está sellado y no admite modificaciones.'}
                        </p>
                    </div>
                </div>
            )}

            <div className="flex space-x-1 bg-slate-200/50 p-1.5 rounded-2xl mb-8 overflow-x-auto hide-scrollbar">
                {secciones.map((seccion, i) => {
                    const active = tabActiva === i;
                    return (
                        <button key={seccion.id} type="button" onClick={() => setTabActiva(i)}
                                className={`flex items-center justify-center gap-2 flex-1 min-w-[180px] py-3 px-4 rounded-xl text-sm font-bold transition-all duration-300 ${active ? 'bg-white text-clinica-primary shadow-sm ring-1 ring-slate-200/50' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'}`}>
                            <span className={active ? 'text-clinica-primary' : 'text-slate-400'}>{ICONOS[seccion.icono] || ICONOS.subjetivo}</span>
                            {i + 1}. {seccion.titulo}
                        </button>
                    );
                })}
            </div>

            {secciones[tabActiva] && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                    {secciones[tabActiva].campos.map(campo => {
                        const ancho = campo.ancho === 'completo' || ['textarea', 'casillas', 'odontograma', 'constantes', 'escala'].includes(campo.tipo);
                        return (
                            <div key={campo.id} className={ancho ? 'md:col-span-2' : ''}>
                                <CampoClinico campo={campo} valor={valorDe(campo)} onChange={(v) => cambiar(campo, v)} disabled={isReadOnly} />
                            </div>
                        );
                    })}
                </div>
            )}

            {!isReadOnly && (
                <div className="mt-8 flex justify-end">
                    <button type="button" onClick={handlePreSubmit} disabled={loading} className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-black text-white bg-emerald-500 hover:bg-emerald-600 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-emerald-500/20">
                        {loading ? (
                            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div><span>Verificando...</span></>
                        ) : (
                            <><SaveIcon fontSize="small" /><span>Finalizar y firmar</span></>
                        )}
                    </button>
                </div>
            )}

            {showConfirmModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in" style={{ margin: 0 }}>
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 border border-slate-200">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-14 h-14 bg-amber-100 text-amber-600 rounded-2xl flex items-center justify-center shrink-0">
                                <GavelIcon fontSize="large" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-slate-900 tracking-tight">Confirmación de cierre</h3>
                                <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mt-1">Advertencia legal</p>
                            </div>
                        </div>
                        <p className="text-sm text-slate-600 font-medium leading-relaxed mb-8">
                            Al proceder, este acto clínico quedará <strong className="text-slate-900">sellado digitalmente</strong> y pasará a formar parte del historial inmutable del paciente. No podrá modificarlo ni eliminarlo en el futuro.
                            <br /><br />
                            ¿Está seguro de que todos los datos son correctos y definitivos?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowConfirmModal(false)} className="px-5 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-colors outline-none">
                                Revisar de nuevo
                            </button>
                            <button onClick={handleConfirmSubmit} className="px-6 py-2.5 rounded-xl font-black text-white bg-slate-900 hover:bg-black transition-colors shadow-lg flex items-center gap-2 outline-none">
                                <LockIcon fontSize="small" /> Sí, firmar definitivamente
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormularioClinico;
